import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useParams } from "react-router-dom"
import { Formik, Form } from "formik"
import * as yup from "yup"
import Select from "react-select"
import { Bounce, toast } from "react-toastify"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import {
  customStyles,
  ChevronDropdown,
} from "../../../components/OnboardingComponents/RoleSelect"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { reportFormApiError } from "../../../helpers/formFieldErrors"

const FORM_ID = "admin-city-form"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New_York (EST)" },
  { value: "America/Chicago", label: "America/Chicago (CST)" },
  { value: "America/Denver", label: "America/Denver (MST)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PST)" },
  { value: "America/Toronto", label: "America/Toronto (EST)" },
  { value: "America/Mexico_City", label: "America/Mexico_City (CST)" },
  { value: "Europe/London", label: "Europe/London (GMT)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET)" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm (CET)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
  { value: "Asia/Seoul", label: "Asia/Seoul (KST)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (SAST)" },
]

const COVER_MAX_BYTES = 5120 * 1024 // 5120 KB
const COVER_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp"
const COVER_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"]

const EMPTY_VALUES = {
  city: "",
  state: "",
  country: "",
  timezone: "",
  tagline: "",
  description: "",
  cover: "",
}

const CITY_API_FIELD_MAP = {
  cover_media: "cover",
}

const cityFieldSchema = {
  city: yup
    .string()
    .trim()
    .required("City name is required")
    .max(100, "City must be at most 100 characters"),
  state: yup
    .string()
    .trim()
    .required("State/region is required")
    .max(100, "State must be at most 100 characters"),
  country: yup
    .string()
    .trim()
    .required("Country is required")
    .max(100, "Country must be at most 100 characters"),
  timezone: yup
    .string()
    .trim()
    .required("Timezone is required")
    .max(90, "Timezone must be at most 90 characters"),
  tagline: yup
    .string()
    .trim()
    .required("Tagline is required")
    .max(250, "Tagline must be at most 250 characters"),
  description: yup
    .string()
    .trim()
    .max(500, "Description must be at most 500 characters"),
  cover: yup.string().trim().required("Cover photo is required"),
}

const buildAddSchema = () => yup.object().shape(cityFieldSchema)
const buildEditSchema = () => yup.object().shape(cityFieldSchema)

/**
 * Build multipart payload for admin cities create/update.
 * Create: POST /api/v1/admin/cities
 * Update: POST /api/v1/admin/cities/:cityId
 * country, state, city, timezone, tagline, cover required; description optional.
 */
const buildCityFormData = (values, coverFile) => {
  const formData = new FormData()

  formData.append("city", String(values.city || "").trim())
  formData.append("state", String(values.state || "").trim())
  formData.append("country", String(values.country || "").trim())
  formData.append("timezone", String(values.timezone || "").trim())
  formData.append("tagline", String(values.tagline || "").trim())

  const description = String(values.description || "").trim()
  if (description) formData.append("description", description)

  if (coverFile) {
    formData.append("cover_media", coverFile)
  }

  return formData
}

const mapCityToFormValues = city => ({
  city: city?.city || "",
  state: city?.state || "",
  country: city?.country || "",
  timezone: city?.timezone || "",
  tagline: city?.tagline || "",
  description: city?.description || "",
  cover: city?.cover_media_url || city?.thumbnail_path_url || "",
})

const isAllowedCoverType = file => {
  if (!file) return false
  if (COVER_MIME_TYPES.includes(file.type)) return true
  const ext = String(file.name || "")
    .split(".")
    .pop()
    ?.toLowerCase()
  return ["png", "jpg", "jpeg", "webp"].includes(ext)
}
const TimezoneSelect = ({ value, onChange, onBlur, error, required }) => {
  const selected =
    TIMEZONE_OPTIONS.find(option => option.value === value) || null
  const selectStyles = customStyles(!!error)

  return (
    <div className={`amok-field ${error ? "amok-field--error" : ""}`.trim()}>
      <label className="amok-field__label">
        Timezone
        {required ? <span className="amok-field__required"> *</span> : null}
      </label>
      <Select
        className={`amok-role-select ${error ? "amok-role-select--error" : ""}`}
        classNamePrefix="amok-role-select"
        options={TIMEZONE_OPTIONS}
        value={selected}
        onChange={option => onChange(option ? option.value : "")}
        onBlur={onBlur}
        placeholder="Select timezone"
        isSearchable
        isClearable={false}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        components={{
          DropdownIndicator: ChevronDropdown,
          IndicatorSeparator: null,
        }}
        styles={{
          ...selectStyles,
          menuPortal: base => ({ ...base, zIndex: 10060 }),
        }}
      />
      {error ? (
        <p className="amok-field__message amok-field__message--error">{error}</p>
      ) : null}
    </div>
  )
}

TimezoneSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  required: PropTypes.bool,
}

TimezoneSelect.defaultProps = {
  value: "",
  onBlur: undefined,
  error: "",
  required: false,
}

/**
 * Shared Add / Edit City form — wires admin cities create/update APIs.
 */
const CityForm = ({ mode }) => {
  const navigate = useNavigate()
  const { cityId } = useParams()
  const isEdit = mode === "edit"
  const fileInputRef = useRef(null)

  const [saving, setSaving] = useState(false)
  const [loadingCity, setLoadingCity] = useState(isEdit)
  const [coverPreview, setCoverPreview] = useState(null)
  const [coverFile, setCoverFile] = useState(null)
  const [initialValues, setInitialValues] = useState(EMPTY_VALUES)

  useEffect(() => {
    document.title = `${isEdit ? "Edit City" : "Add City"} | AMOK . EARTH Admin`
  }, [isEdit])

  useEffect(() => {
    if (!isEdit) {
      setInitialValues(EMPTY_VALUES)
      setCoverPreview(null)
      setCoverFile(null)
      setLoadingCity(false)
      return undefined
    }

    let cancelled = false
    const fetchCity = async () => {
      setLoadingCity(true)
      try {
        // API has no GET /cities/:id — resolve from cities list.
        const response = await get(endpoints.getAdminCities, {
          params: { page: 1, page_size: 100 },
        })
        if (cancelled) return
        if (response?.data?.success) {
          const rows = response.data.result?.data || []
          const city = rows.find(item => String(item.id) === String(cityId))
          if (!city) {
            toast.error("City not found.", TOAST_OPTIONS)
            navigate("/admin/cities")
            return
          }
          setInitialValues(mapCityToFormValues(city))
          setCoverPreview(
            city.cover_media_url || city.thumbnail_path_url || null,
          )
          setCoverFile(null)
        } else {
          toastApiError(response, "Failed to load city")
          navigate("/admin/cities")
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load city")
          navigate("/admin/cities")
        }
      } finally {
        if (!cancelled) setLoadingCity(false)
      }
    }

    fetchCity()
    return () => {
      cancelled = true
    }
  }, [cityId, isEdit, navigate])

  const handleCoverChange = (e, { setFieldValue, setFieldError, setFieldTouched }) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!isAllowedCoverType(file)) {
      e.target.value = ""
      setFieldTouched("cover", true, false)
      setFieldError("cover", "Cover must be PNG, JPG, JPEG, or WEBP.")
      return
    }

    if (file.size > COVER_MAX_BYTES) {
      e.target.value = ""
      setFieldTouched("cover", true, false)
      setFieldError("cover", "Cover image must be 5120 KB or smaller.")
      return
    }

    setCoverFile(file)
    setCoverPreview(prev => {
      if (prev?.startsWith?.("blob:")) URL.revokeObjectURL(prev)
      return URL.createObjectURL(file)
    })
    setFieldValue("cover", file.name)
    setFieldError("cover", undefined)
  }

  const handleDeleteCover = ({ setFieldValue, setFieldTouched }) => {
    setCoverFile(null)
    setCoverPreview(prev => {
      if (prev?.startsWith?.("blob:")) URL.revokeObjectURL(prev)
      return null
    })
    if (fileInputRef.current) fileInputRef.current.value = ""
    setFieldValue("cover", "")
    setFieldTouched("cover", true)
  }

  const handleSubmit = async (values, { setFieldError, setFieldTouched }) => {
    if (saving) return

    if (!coverFile && !coverPreview) {
      setFieldTouched("cover", true, false)
      setFieldError("cover", "Cover photo is required.")
      return
    }

    setSaving(true)
    try {
      const formData = buildCityFormData(values, coverFile)
      const response = isEdit
        ? await post(endpoints.updateAdminCity(cityId), formData, {
            skipErrorToast: true,
          })
        : await post(endpoints.createAdminCity, formData, {
            skipErrorToast: true,
          })

      if (response?.data?.success) {
        toast.success(
          response.data.message ||
            (isEdit
              ? "City updated successfully."
              : "City created successfully."),
          TOAST_OPTIONS,
        )
        navigate("/admin/cities")
      } else {
        reportFormApiError(
          response,
          isEdit ? "Failed to update city" : "Failed to create city",
          { setFieldError, setFieldTouched },
          CITY_API_FIELD_MAP,
        )
      }
    } catch (err) {
      reportFormApiError(
        err,
        isEdit ? "Failed to update city" : "Failed to create city",
        { setFieldError, setFieldTouched },
        CITY_API_FIELD_MAP,
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-page admin-city-form">
      <Formik
        initialValues={initialValues}
        validationSchema={isEdit ? buildEditSchema() : buildAddSchema()}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          setFieldTouched,
          setFieldError,
          isSubmitting,
        }) => {
          const coverInvalid = Boolean(touched.cover && errors.cover)

          return (
          <>
            <PageHeader
              className="admin-city-form__header"
              title={isEdit ? "Edit City" : "Add City"}
              subtitle={
                isEdit
                  ? "Update city information."
                  : "Add a new city to the platform"
              }
              actions={
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  form={FORM_ID}
                  loading={saving || isSubmitting || loadingCity}
                  disabled={saving || isSubmitting || loadingCity}
                >
                  {isEdit ? "Update City" : "Create City"}
                </CustomButton>
              }
            />

            <Form id={FORM_ID} className="admin-city-form__content">
              <div className="admin-city-form__fields">
                <div className="admin-city-form__row">
                  <CustomInput
                    label="City Name"
                    name="city"
                    required
                    placeholder={
                      isEdit ? "Enter city name" : "Search City by name"
                    }
                    value={values.city}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("city", true)}
                    error={touched.city && errors.city}
                  />
                  <CustomInput
                    label="State/Region"
                    name="state"
                    required
                    placeholder="Enter state/region"
                    value={values.state}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("state", true)}
                    error={touched.state && errors.state}
                  />
                </div>

                <div className="admin-city-form__row">
                  <CustomInput
                    label="Country"
                    name="country"
                    required
                    placeholder="Enter Country"
                    value={values.country}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("country", true)}
                    error={touched.country && errors.country}
                  />
                  <TimezoneSelect
                    value={values.timezone}
                    onChange={value => setFieldValue("timezone", value)}
                    onBlur={() => setFieldTouched("timezone", true)}
                    error={touched.timezone && errors.timezone}
                    required
                  />
                </div>

                <CustomInput
                  label="Tagline"
                  name="tagline"
                  required
                  placeholder="Short tagline for this city"
                  value={values.tagline}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("tagline", true)}
                  error={touched.tagline && errors.tagline}
                />

                <CustomInput
                  label="Description"
                  name="description"
                  textarea
                  rows={4}
                  placeholder="Tell people about city, creative background and what city is famous about..."
                  value={values.description}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("description", true)}
                  error={touched.description && errors.description}
                />
              </div>

              <section className="admin-city-form__cover-section">
                <h2 className="admin-city-form__cover-title">
                  Cover Photo
                  <span className="amok-field__required"> *</span>
                </h2>
                <div
                  className={`admin-city-form__cover ${
                    coverPreview ? "admin-city-form__cover--filled" : ""
                  } ${coverInvalid ? "admin-city-form__cover--error" : ""}`.trim()}
                >
                  {coverPreview ? (
                    <img
                      src={coverPreview}
                      alt=""
                      className="admin-city-form__cover-image"
                    />
                  ) : null}
                  <div className="admin-city-form__cover-overlay">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={COVER_ACCEPT}
                      className="admin-city-form__file-input"
                      onChange={e =>
                        handleCoverChange(e, {
                          setFieldValue,
                          setFieldError,
                          setFieldTouched,
                        })
                      }
                    />
                    <CustomButton
                      variant={coverPreview ? "secondary" : "ghost"}
                      size="sm"
                      type="button"
                      className="admin-city-form__upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload Cover Photo
                    </CustomButton>
                    <p
                      className={`admin-city-form__cover-hint ${
                        coverPreview
                          ? "admin-city-form__cover-hint--on-image"
                          : ""
                      }`}
                    >
                      PNG, JPG, JPEG or WEBP. Max size of 5120 KB
                    </p>
                    {coverPreview ? (
                      <CustomButton
                        variant="danger"
                        size="sm"
                        type="button"
                        className="admin-city-form__delete-btn"
                        onClick={() =>
                          handleDeleteCover({ setFieldValue, setFieldTouched })
                        }
                      >
                        Delete
                      </CustomButton>
                    ) : null}
                  </div>
                </div>
                {coverInvalid ? (
                  <p className="amok-field__message amok-field__message--error">
                    {errors.cover}
                  </p>
                ) : null}
              </section>
            </Form>
          </>
          )
        }}
      </Formik>
    </div>
  )
}

CityForm.propTypes = {
  mode: PropTypes.oneOf(["add", "edit"]).isRequired,
}

export const AdminAddCity = () => <CityForm mode="add" />
export const AdminEditCity = () => <CityForm mode="edit" />

export default CityForm
